/**
 * SNAP CHART AI - Prompt System Index
 * Central export point for all prompt generators.
 * 
 * Architecture:
 * - corePrompt.ts: Base institutional prompt (SNAP_CHART_CORE_PROMPT)
 * - goldPrompt.ts: Gold-specific extensions
 * - indicesPrompts.ts: US30, NAS100, SPP500, GER30 extensions
 * - cryptoPrompts.ts: BTC, ETH, SOL, and altcoin extensions
 */

// Core prompt exports
export {
  SNAP_CHART_CORE_PROMPT,
  getCurrentSession,
  formatPrice,
  getMarketStatus
} from './corePrompt';
import { getMarketStatus, type AssetType } from './corePrompt';

// Gold prompt exports
export { 
  generateGoldPrompt, 
  GOLD_PROMPT 
} from './goldPrompt';

// Indices prompt exports
export {
  generateUS30Prompt,
  generateNAS100Prompt,
  generateSPP500Prompt,
  generateGER30Prompt,
  generateIndicesPrompt,
  US30_PROMPT,
  NAS100_PROMPT,
  SPP500_PROMPT,
  GER30_PROMPT,
  INDICES_PROMPT,
} from './indicesPrompts';

// Crypto prompt exports
export {
  generateBTCPrompt,
  generateETHPrompt,
  generateSOLPrompt,
  generateAltcoinPrompt,
  generateCryptoPrompt,
  BTC_PROMPT,
  ETH_PROMPT,
  SOL_PROMPT,
  ALTCOIN_PROMPT,
  CRYPTO_PROMPT,
} from './cryptoPrompts';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// PROMPT BUILDER - Main Entry Point
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import { generateGoldPrompt } from './goldPrompt';
import { 
  generateUS30Prompt, 
  generateNAS100Prompt, 
  generateSPP500Prompt, 
  generateGER30Prompt,
  generateIndicesPrompt 
} from './indicesPrompts';
import { 
  generateBTCPrompt, 
  generateETHPrompt, 
  generateSOLPrompt,
  generateAltcoinPrompt,
  generateCryptoPrompt 
} from './cryptoPrompts';

export interface PromptGeneratorOptions {
  currentPrice: number;
  session?: string;
  customContext?: string;
}

/**
 * Main prompt builder that routes to the correct asset-specific prompt generator.
 * This is the primary function to use when generating prompts from the frontend.
 * 
 * @param asset - The market/asset identifier (e.g., 'gold', 'btcusd', 'us30')
 * @param options - Configuration including currentPrice and optional session override
 * @returns The complete prompt string for the specified asset
 */
/** Map a (normalized) asset id to its market type for market-hours detection. */
function assetTypeOf(asset: string): AssetType {
  const a = asset.toLowerCase().trim();
  if (a === 'gold' || a === 'xauusd') return 'gold';
  const indices = ['us30', 'dji', 'dow', 'nas100', 'nasdaq', 'nq', 'spp500', 'spx', 'sp500', 'ger30', 'dax', 'ger40', 'indices'];
  if (indices.includes(a)) return 'indices';
  return 'crypto'; // btc/eth/alts/crypto/default — 24/7
}

export function buildPrompt(asset: string, options: PromptGeneratorOptions): string {
  const { currentPrice, session, customContext } = options;
  const normalizedAsset = asset.toLowerCase().trim();
  
  let prompt: string;
  
  // Route to the appropriate prompt generator
  switch (normalizedAsset) {
    // Gold
    case 'gold':
    case 'xauusd':
      prompt = generateGoldPrompt(currentPrice, session);
      break;
    
    // Indices
    case 'us30':
    case 'dji':
    case 'dow':
      prompt = generateUS30Prompt(currentPrice, session);
      break;
    
    case 'nas100':
    case 'nasdaq':
    case 'nq':
      prompt = generateNAS100Prompt(currentPrice, session);
      break;
    
    case 'spp500':
    case 'spx':
    case 'sp500':
      prompt = generateSPP500Prompt(currentPrice, session);
      break;
    
    case 'ger30':
    case 'dax':
    case 'ger40':
      prompt = generateGER30Prompt(currentPrice, session);
      break;
    
    case 'indices':
      prompt = generateIndicesPrompt(currentPrice, session);
      break;
    
    // Major Cryptocurrencies
    case 'btcusd':
    case 'btc':
    case 'bitcoin':
      prompt = generateBTCPrompt(currentPrice, session);
      break;
    
    case 'ethusd':
    case 'eth':
    case 'ethereum':
      prompt = generateETHPrompt(currentPrice, session);
      break;
    
    case 'solusd':
    case 'sol':
    case 'solana':
      prompt = generateSOLPrompt(currentPrice, session);
      break;
    
    // Altcoins (use generic altcoin generator)
    case 'xrpusd':
    case 'xrp':
    case 'adausd':
    case 'ada':
    case 'dogeusd':
    case 'doge':
    case 'avaxusd':
    case 'avax':
    case 'linkusd':
    case 'link':
    case 'maticusd':
    case 'matic':
    case 'dotusd':
    case 'dot':
      prompt = generateAltcoinPrompt(normalizedAsset.replace('usd', '') + 'usd', currentPrice, session);
      break;
    
    // Generic crypto fallback
    case 'crypto':
      prompt = generateCryptoPrompt(currentPrice, session);
      break;
    
    // Default fallback - try as altcoin
    default:
      console.warn(`Unknown asset: ${asset}, using generic crypto prompt`);
      prompt = generateCryptoPrompt(currentPrice, session);
  }
  
  // Append custom context if provided
  if (customContext) {
    prompt += `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📝 ADDITIONAL CONTEXT FROM USER
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${customContext}`;
  }

  // Inject authoritative market-open/closed status (discipline guard).
  const marketStatus = getMarketStatus(assetTypeOf(normalizedAsset));
  prompt += `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📅 MARKET STATUS (authoritative — provided by the application)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
MARKET STATUS: ${marketStatus.label} — ${marketStatus.detail}
${marketStatus.isOpen
  ? 'The market is open; live execution is possible if all conditions align.'
  : '⚠️ The market is CLOSED. You MUST output Decision: NO_TRADE (or WAIT) and MUST NOT output TRADE_APPROVED. State clearly that the market is closed; any levels are for PREPARATION ONLY, not live execution.'}`;

  return prompt;
}

/**
 * Get the list of all supported assets
 */
export function getSupportedAssets(): string[] {
  return [
    // Precious Metals
    'gold',
    // Indices
    'us30', 'nas100', 'spp500', 'ger30', 'indices',
    // Crypto
    'btcusd', 'ethusd', 'solusd', 'xrpusd', 'adausd', 
    'dogeusd', 'avaxusd', 'linkusd', 'maticusd', 'dotusd', 'crypto'
  ];
}

/**
 * Check if an asset is supported by the prompt system
 */
export function isAssetSupported(asset: string): boolean {
  const normalizedAsset = asset.toLowerCase().trim();
  const aliases: Record<string, boolean> = {
    'gold': true, 'xauusd': true,
    'us30': true, 'dji': true, 'dow': true,
    'nas100': true, 'nasdaq': true, 'nq': true,
    'spp500': true, 'spx': true, 'sp500': true,
    'ger30': true, 'dax': true, 'ger40': true,
    'indices': true,
    'btcusd': true, 'btc': true, 'bitcoin': true,
    'ethusd': true, 'eth': true, 'ethereum': true,
    'solusd': true, 'sol': true, 'solana': true,
    'xrpusd': true, 'xrp': true,
    'adausd': true, 'ada': true,
    'dogeusd': true, 'doge': true,
    'avaxusd': true, 'avax': true,
    'linkusd': true, 'link': true,
    'maticusd': true, 'matic': true,
    'dotusd': true, 'dot': true,
    'crypto': true,
  };
  
  return !!aliases[normalizedAsset];
}
